import {
  type FC,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';

import styles from './PageScrollbar.module.scss';

const FADE_MS = 650;
const EDGE_SHOW_PX = 12;
const MIN_THUMB_PX = 40;

/**
 * Overlay replacement for the page scrollbar.
 *
 * A native classic scrollbar reserves an 8px strip, and every transition
 * that toggles the reservation (chat open/close, dock, pin) reflowed the
 * page by that width. The root and workspace page scrollers therefore
 * render with no native scrollbar at all — zero reservation in every
 * state — and this widget draws a floating thumb instead: absolutely
 * positioned and layout-neutral, shown while scrolling or while the
 * pointer hovers the screen edge, draggable to scroll.
 */
export const PageScrollbar: FC<{
  scrollerRef: RefObject<HTMLDivElement | null>;
  pinned: boolean;
}> = ({ scrollerRef, pinned }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const hideTimer = useRef<number | undefined>(undefined);
  const dragRef = useRef<{ startY: number; startScroll: number } | null>(null);

  // The active page scroller: the workspace scrollArea while pinned (the
  // root then holds the fixed workspace and cannot scroll), the root
  // scroller otherwise. Falls back to whichever one actually overflows.
  const activeScroller = (): HTMLElement | null => {
    const el = scrollerRef.current;
    if (pinned && el && el.scrollHeight - el.clientHeight > 1) {
      return el;
    }
    return (document.scrollingElement as HTMLElement | null) ?? null;
  };

  /** Position/size the thumb; reports whether the page can scroll at all. */
  const update = (): boolean => {
    const el = activeScroller();
    const thumb = thumbRef.current;
    if (!el || !thumb) {
      return false;
    }
    const overflow = el.scrollHeight - el.clientHeight;
    if (overflow <= 1) {
      setVisible(false);
      return false;
    }
    const isRoot = el === document.scrollingElement;
    const track = isRoot
      ? window.innerHeight
      : el.getBoundingClientRect().height;
    const size = Math.max(
      MIN_THUMB_PX,
      track * (el.clientHeight / el.scrollHeight),
    );
    const top = ((track - size) * el.scrollTop) / overflow;
    const right = isRoot
      ? 0
      : Math.max(0, window.innerWidth - el.getBoundingClientRect().right);
    thumb.style.height = `${size}px`;
    thumb.style.top = `${top}px`;
    thumb.style.right = `${right}px`;
    return true;
  };

  /** Show the thumb and arm the fade-out timer. */
  const show = () => {
    clearTimeout(hideTimer.current);
    if (update()) {
      setVisible(true);
    }
    hideTimer.current = window.setTimeout(() => {
      if (!dragRef.current) {
        setVisible(false);
      }
    }, FADE_MS);
  };

  const pinnedPrev = useRef(pinned);
  useEffect(() => {
    // While this overlay scrollbar manages the page, the native html
    // scrollbar stays hidden (scrollbar.scss keys off this class). Without
    // the marker, pages outside the chat workspace would scroll with no
    // visible scrollbar at all.
    document.documentElement.classList.add('agent-overlay-scrollbar-active');
    const onScroll = (event: Event) => {
      const target = event.target;
      // Root scrolls report `document`; the workspace scroller reports
      // itself. Internal scrollers (modals, lists) are ignored.
      if (target === document || target === scrollerRef.current) {
        show();
      }
    };
    const onPointerMove = (event: PointerEvent) => {
      if (dragRef.current) {
        return;
      }
      if (event.clientX >= window.innerWidth - EDGE_SHOW_PX) {
        show();
      }
    };
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('resize', update);
    // Pin/unpin flips the active scroller without a scroll event: flash the
    // thumb at its new position so the affordance stays discoverable.
    if (pinnedPrev.current !== pinned) {
      pinnedPrev.current = pinned;
      show();
    } else {
      update();
    }
    return () => {
      document.documentElement.classList.remove(
        'agent-overlay-scrollbar-active',
      );
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('resize', update);
      clearTimeout(hideTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinned, scrollerRef]);

  const onThumbPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    const el = activeScroller();
    if (!el || event.button !== 0) {
      return;
    }
    event.preventDefault();
    dragRef.current = { startY: event.clientY, startScroll: el.scrollTop };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onThumbPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const el = activeScroller();
    const thumb = thumbRef.current;
    if (!drag || !el || !thumb) {
      return;
    }
    const track =
      trackRef.current?.getBoundingClientRect().height ?? window.innerHeight;
    const size = thumb.offsetHeight;
    const maxScroll = el.scrollHeight - el.clientHeight;
    const dy = event.clientY - drag.startY;
    el.scrollTop =
      drag.startScroll + (dy / Math.max(1, track - size)) * maxScroll;
  };

  const onThumbPointerEnd = () => {
    dragRef.current = null;
  };

  return (
    <div
      ref={trackRef}
      className={styles.track}
      data-visible={visible || undefined}
      aria-hidden="true"
    >
      <div
        ref={thumbRef}
        className={styles.thumb}
        data-testid="page-scrollbar-thumb"
        onPointerDown={onThumbPointerDown}
        onPointerMove={onThumbPointerMove}
        onPointerUp={onThumbPointerEnd}
        onPointerCancel={onThumbPointerEnd}
      />
    </div>
  );
};
